import React from 'react';
import { cn } from '../../lib/utils';
import { Slot } from '@radix-ui/react-slot';
import { usePageContext, generateAutoTrackingId, pushGTMEvent } from '../../utils/tracking';

export interface ButtonProps
  extends React.ButtonHTMLAttributes<HTMLButtonElement> {
  asChild?: boolean;
  variant?: 'default' | 'destructive' | 'outline' | 'secondary' | 'ghost' | 'link';
  size?: 'default' | 'sm' | 'lg' | 'icon';
  // Tracking props
  trackingId?: string; // Custom tracking ID override
  trackingSection?: string; // Section context for tracking
  disableTracking?: boolean; // Disable automatic tracking
}

const Button = React.forwardRef<HTMLButtonElement, ButtonProps>(
  ({ 
    className, 
    variant = 'default', 
    size = 'default', 
    asChild = false, 
    children,
    onClick,
    trackingId,
    trackingSection,
    disableTracking = false,
    ...props 
  }, ref) => {
    const { pageName, section } = usePageContext();
    
    // Generate tracking ID if not provided and tracking is enabled
    const autoTrackingId = !disableTracking && !trackingId ? 
      generateAutoTrackingId({
        elementType: 'button',
        pageName,
        children,
        variant,
        section: trackingSection || section,
        ariaLabel: props['aria-label'],
        title: props.title
      }) : trackingId;

    // Enhanced onClick handler with tracking
    const handleClick = (event: React.MouseEvent<HTMLButtonElement>) => {
      // Push GTM event if tracking is enabled
      if (!disableTracking && autoTrackingId) {
        pushGTMEvent({
          elementType: 'button',
          elementId: autoTrackingId,
          elementText: typeof children === 'string' ? children : undefined,
          pageName,
          section: trackingSection || section,
          variant,
          userAction: 'click'
        });
      }
      
      // Call original onClick handler
      if (onClick) {
        onClick(event);
      }
    };

    const Comp = asChild ? Slot : 'button';
    return (
      <Comp
        className={cn(
          'inline-flex items-center justify-center rounded-lg text-sm font-medium transition-colors focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-blue-500 disabled:pointer-events-none disabled:opacity-50',
          {
            'bg-blue-600 text-white shadow hover:bg-blue-700 dark:bg-blue-600 dark:hover:bg-blue-700': variant === 'default',
            'bg-red-600 text-white shadow-sm hover:bg-red-700 dark:bg-red-600 dark:hover:bg-red-700': variant === 'destructive',
            'border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-800 text-gray-700 dark:text-gray-200 shadow-sm hover:bg-gray-50 dark:hover:bg-gray-700 hover:text-gray-900 dark:hover:text-white': variant === 'outline',
            'bg-gray-100 dark:bg-gray-700 text-gray-900 dark:text-gray-100 shadow-sm hover:bg-gray-200 dark:hover:bg-gray-600': variant === 'secondary',
            'text-gray-700 dark:text-gray-200 hover:bg-gray-100 dark:hover:bg-gray-700 hover:text-gray-900 dark:hover:text-white': variant === 'ghost',
            'text-blue-600 dark:text-blue-400 underline-offset-4 hover:underline': variant === 'link',
          },
          {
            'h-9 px-4 py-2': size === 'default',
            'h-8 rounded-md px-3 text-xs': size === 'sm',
            'h-10 rounded-md px-8': size === 'lg',
            'h-9 w-9': size === 'icon',
          },
          className
        )}
        ref={ref}
        onClick={handleClick}
        id={autoTrackingId}
        data-tracking-id={autoTrackingId}
        {...props}
      >
        {children}
      </Comp>
    );
  }
);
Button.displayName = 'Button';

export { Button };